/** MÓDULO CENTRAL CLIENTES — ficha maestra (CL-XXXX).
 *  Tabs Todos / SA / CYH · detección de duplicados · un vendedor por cliente ·
 *  ficha con leads vinculados, presupuestos SA y actividades. */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MagnifyingGlass, Plus, UserCircle, CircleNotch } from '@phosphor-icons/react';

const SUP = ['admin', 'supervisor', 'gerente'];
const errMsg = (e, def) => e?.response?.data?.detail || def;

const EmpresaChips = ({ c }) => (
  <span className="inline-flex gap-1">
    {c.empresa_sa && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-600 border border-sky-500/30">SA</span>}
    {c.empresa_cyh && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 border border-amber-500/30">CYH</span>}
    {!c.empresa_sa && !c.empresa_cyh && <span className="text-[10px] text-[var(--text-faint)]">sin asignar</span>}
  </span>
);

const ORIGEN = { vendedor: 'Vendedor', redes: 'Redes sociales', manual: 'Manual' };

export default function Clientes() {
  const { user } = useAuth();
  const esSup = SUP.includes((user?.role || '').toLowerCase());
  const [tab, setTab] = useState('');
  const [q, setQ] = useState('');
  const [agrupar, setAgrupar] = useState(false);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevo, setNuevo] = useState(false);
  const [ficha, setFicha] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    api.get('/clientes', { params: { q, empresa: tab, limit: 300 } })
      .then((r) => setLista(r.data.clientes || []))
      .catch(() => toast.error('No se pudieron cargar los clientes'))
      .finally(() => setLoading(false));
  }, [q, tab]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  return (
    <div className="p-3 sm:p-5 space-y-3" data-testid="clientes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-black text-[var(--text)]">Clientes</h1>
          <p className="text-[11px] text-[var(--text-muted)]">Ficha maestra · un cliente = una ficha, un vendedor asignado</p>
        </div>
        <Button onClick={() => setNuevo(true)} className="bg-[#1D4ED8] hover:bg-[#1e40af] text-white" data-testid="cli-nuevo-btn">
          <Plus size={15} className="mr-1" /> Nuevo cliente
        </Button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-lg border border-[var(--border)] overflow-hidden">
          {[['', 'Todos'], ['SA', 'SA — Soluciones'], ['CYH', 'CYH — Complementos']].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`cli-tab-${v || 'todos'}`}
              className={`px-3 py-1.5 text-xs font-bold ${tab === v ? 'bg-[#1D4ED8] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--bg)]'}`}>{l}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" />
          <Input className="pl-8 h-9 text-sm" placeholder="Buscar por nombre, teléfono, WhatsApp, correo o ID…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="cli-buscar" />
        </div>
        <span className="text-[11px] text-[var(--text-muted)]" data-testid="cli-count">{lista.length} cliente(s)</span>
        <label className="flex items-center gap-1 text-[11px] font-bold text-[var(--text-muted)] cursor-pointer">
          <input type="checkbox" checked={agrupar} onChange={(e) => setAgrupar(e.target.checked)} data-testid="cli-agrupar" /> Agrupar por vendedor
        </label>
        <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="cli-pdf"
          onClick={async () => { try { const r = await api.get('/clientes/pdf', { responseType: 'blob' }); const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = 'clientes.pdf'; a.click(); URL.revokeObjectURL(u); } catch { toast.error('No se pudo generar el PDF'); } }}>
          Exportar PDF
        </Button>
      </div>

      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center"><CircleNotch size={22} className="animate-spin inline text-[#1D4ED8]" /></div> : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
                  <th className="px-3 py-2">ID</th><th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Empresa</th>
                  <th className="px-3 py-2">Teléfono</th><th className="px-3 py-2">Correo</th><th className="px-3 py-2">Dirección</th>
                  <th className="px-3 py-2">Crédito</th>
                  <th className="px-3 py-2">Vendedor</th><th className="px-3 py-2">Origen</th>
                </tr></thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {(agrupar ? [...lista].sort((a, b) => (a.vendedor_nombre || '~').localeCompare(b.vendedor_nombre || '~')) : lista).map((c, i, arr) => (
                    <React.Fragment key={c.id}>
                      {agrupar && (i === 0 || (arr[i - 1].vendedor_nombre || '') !== (c.vendedor_nombre || '')) && (
                        <tr><td colSpan={9} className="px-3 py-1.5 text-[10px] font-black uppercase bg-[var(--bg)] text-[#1D4ED8]" data-testid={`cli-grupo-${c.vendedor_nombre || 'libres'}`}>
                          {c.vendedor_nombre || 'SIN VENDEDOR (libres)'} · {arr.filter((x) => (x.vendedor_nombre || '') === (c.vendedor_nombre || '')).length}
                        </td></tr>
                      )}
                    <tr className="hover:bg-[var(--bg)] cursor-pointer" onClick={() => setFicha(c.id)} data-testid={`cli-row-${c.id}`}>
                      <td className="px-3 py-2 font-mono text-xs text-[#1D4ED8] font-bold">{c.id}</td>
                      <td className="px-3 py-2 font-bold text-[var(--text)]">{c.nombre}</td>
                      <td className="px-3 py-2"><EmpresaChips c={c} /></td>
                      <td className="px-3 py-2 text-xs">{c.telefono || c.whatsapp || '—'}</td>
                      <td className="px-3 py-2 text-xs text-[var(--text-muted)]">{c.correo || '—'}</td>
                      <td className="px-3 py-2 text-xs text-[var(--text-muted)] max-w-[180px] truncate">{c.direccion || '—'}</td>
                      <td className="px-3 py-2 text-xs">{c.credito ? <span className="text-emerald-600 font-bold">Sí</span> : 'No'}</td>
                      <td className="px-3 py-2 text-xs">{c.vendedor_nombre || <span className="text-emerald-600 font-bold">libre</span>}</td>
                      <td className="px-3 py-2 text-[10px] text-[var(--text-muted)]">{ORIGEN[c.origen] || c.origen}</td>
                    </tr>
                    </React.Fragment>
                  ))}
                  {lista.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-[var(--text-muted)] text-xs">Sin clientes. Crea el primero o ejecuta la migración desde vendedores.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-[var(--border)]">
              {lista.map((c) => (
                <button key={c.id} className="w-full text-left p-3" onClick={() => setFicha(c.id)} data-testid={`cli-card-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-black text-sm text-[var(--text)] truncate"><UserCircle size={14} className="inline mr-1 text-[#1D4ED8]" />{c.nombre}</p>
                    <EmpresaChips c={c} />
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">{c.id} · {c.telefono || c.whatsapp || 'sin teléfono'} · {c.vendedor_nombre || 'libre'}</p>
                </button>
              ))}
              {lista.length === 0 && <p className="p-4 text-center text-xs text-[var(--text-muted)]">Sin clientes.</p>}
            </div>
          </>
        )}
      </section>

      {nuevo && <NuevoCliente esSup={esSup} onClose={() => setNuevo(false)} onCreated={() => { setNuevo(false); cargar(); }} onVer={(id) => { setNuevo(false); setFicha(id); }} />}
      {ficha && <FichaCliente cid={ficha} esSup={esSup} onClose={() => setFicha(null)} onChanged={cargar} />}
    </div>
  );
}

const CAMPOS = [['nombre', 'Nombre *'], ['telefono', 'Teléfono'], ['whatsapp', 'WhatsApp'], ['correo', 'Correo'],
  ['direccion', 'Dirección'], ['contacto', 'Contacto'], ['notas', 'Notas']];

const NuevoCliente = ({ esSup, onClose, onCreated, onVer }) => {
  const [f, setF] = useState({ nombre: '', telefono: '', whatsapp: '', correo: '', direccion: '', contacto: '', notas: '', empresa_sa: false, empresa_cyh: false });
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  const guardar = async (forzar = false) => {
    if (!f.nombre.trim()) return toast.error('Nombre requerido');
    setSaving(true);
    try {
      const { data } = await api.post('/clientes', { ...f, forzar });
      if (data.duplicado) { setDup(data.duplicado); return; }
      toast.success(`Cliente ${data.id} creado`);
      onCreated();
    } catch (e) { toast.error(errMsg(e, 'No se pudo crear')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto" data-testid="cli-nuevo-dialog">
        <DialogHeader><DialogTitle>Nuevo cliente</DialogTitle></DialogHeader>
        {dup && (
          <div className="border border-amber-500/50 bg-amber-500/10 rounded-lg p-3 space-y-2" data-testid="cli-duplicado-aviso">
            <p className="text-xs font-black text-amber-600">⚠️ Cliente existente encontrado</p>
            <p className="text-xs text-[var(--text)]">{dup.id} — <b>{dup.nombre}</b> · {dup.telefono || dup.correo} · Vendedor: {dup.vendedor_nombre || 'libre'}</p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white" onClick={() => onVer(dup.id)} data-testid="cli-dup-usar">Usar cliente existente</Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => guardar(true)} data-testid="cli-dup-forzar">Crear de todos modos</Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {CAMPOS.map(([k, l]) => (
            <div key={k} className={k === 'nombre' || k === 'direccion' || k === 'notas' ? 'col-span-2' : ''}>
              <label className="text-[10px] font-bold text-[var(--text-muted)]">{l}</label>
              <Input className="h-9 text-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cli-f-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex gap-4 items-center">
          <label className="text-xs font-bold flex items-center gap-1.5"><input type="checkbox" checked={f.empresa_sa} onChange={(e) => setF({ ...f, empresa_sa: e.target.checked })} data-testid="cli-f-sa" /> SA — Soluciones Arquitectónicas</label>
          <label className="text-xs font-bold flex items-center gap-1.5"><input type="checkbox" checked={f.empresa_cyh} onChange={(e) => setF({ ...f, empresa_cyh: e.target.checked })} data-testid="cli-f-cyh" /> CYH — Complementos</label>
        </div>
        <p className="text-[10px] text-[var(--text-muted)]">{esSup ? 'Como supervisor/gerencia puedes dejar el cliente libre (sin vendedor).' : 'El cliente quedará asignado a ti como vendedor.'}</p>
        <Button onClick={() => guardar(false)} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1e40af] text-white w-full" data-testid="cli-guardar-btn">
          {saving ? 'Guardando…' : 'Guardar cliente'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

import { TimelineCliente } from '@/components/TimelineCliente';

const FichaCliente = ({ cid, esSup, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [users, setUsers] = useState([]);
  const cargar = useCallback(() => {
    api.get(`/clientes/${cid}`).then((r) => { setData(r.data); setEdit({ ...r.data.cliente }); }).catch(() => toast.error('No se pudo cargar la ficha'));
  }, [cid]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (esSup) api.get('/users').then((r) => setUsers(r.data || [])).catch(() => {}); }, [esSup]);
  if (!data || !edit) return null;
  const c = data.cliente;
  const vendLibre = !(c.vendedor_id || '');
  const guardar = async () => {
    try {
      const patch = { nombre: edit.nombre, telefono: edit.telefono, whatsapp: edit.whatsapp, correo: edit.correo,
        direccion: edit.direccion, contacto: edit.contacto, notas: edit.notas,
        empresa_sa: edit.empresa_sa, empresa_cyh: edit.empresa_cyh };
      if ((esSup || vendLibre) && edit.vendedor_id !== c.vendedor_id) patch.vendedor_id = edit.vendedor_id || '';
      await api.patch(`/clientes/${cid}`, patch);
      toast.success('Cliente actualizado');
      cargar(); onChanged();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };
  const eliminar = async () => {
    if (!window.confirm('¿Eliminar (desactivar) este cliente? Su historial se conserva.')) return;
    try { await api.delete(`/clientes/${cid}`); toast.success('Cliente eliminado'); onClose(); onChanged(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo eliminar')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="cli-ficha-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{c.id} — {c.nombre} <EmpresaChips c={c} /></DialogTitle></DialogHeader>
        <p className="text-[11px] text-[var(--text-muted)]" data-testid="cli-ficha-origen">
          Origen: <b>{ORIGEN[c.origen] || c.origen}</b>{(c.leads || []).length > 0 && c.origen !== 'redes' && ' (con leads de redes vinculados)'} · Creado {String(c.created_at).slice(0, 10)}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {CAMPOS.map(([k, l]) => (
            <div key={k} className={['nombre', 'direccion', 'notas'].includes(k) ? 'col-span-2' : ''}>
              <label className="text-[10px] font-bold text-[var(--text-muted)]">{l}</label>
              <Input className="h-9 text-sm" value={edit[k] || ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`cli-e-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)]">Vendedor asignado {!esSup && !vendLibre && '(solo supervisores pueden cambiarlo)'}</label>
            {esSup || vendLibre ? (
              <select className="w-full h-9 text-sm border border-[var(--border)] rounded-md bg-[var(--surface)] text-[var(--text)] px-2"
                value={edit.vendedor_id || ''} onChange={(e) => setEdit({ ...edit, vendedor_id: e.target.value })} data-testid="cli-e-vendedor">
                <option value="">— libre —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                {!esSup && <option value={edit.vendedor_id_self || ''} hidden />}
              </select>
            ) : <Input className="h-9 text-sm" value={c.vendedor_nombre || ''} disabled data-testid="cli-e-vendedor-ro" />}
          </div>
          <div className="flex gap-3 items-end pb-1">
            <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={!!edit.empresa_sa} onChange={(e) => setEdit({ ...edit, empresa_sa: e.target.checked })} data-testid="cli-e-sa" /> SA</label>
            <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={!!edit.empresa_cyh} onChange={(e) => setEdit({ ...edit, empresa_cyh: e.target.checked })} data-testid="cli-e-cyh" /> CYH</label>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={guardar} className="bg-[#1D4ED8] hover:bg-[#1e40af] text-white" data-testid="cli-ficha-guardar">Guardar cambios</Button>
          {esSup && <Button variant="outline" className="text-red-500 border-red-500/50 hover:bg-red-500/10" onClick={eliminar} data-testid="cli-ficha-eliminar">Eliminar</Button>}
        </div>

        {(c.leads || []).length > 0 && (
          <div className="border border-[var(--border)] rounded-lg p-2" data-testid="cli-ficha-leads">
            <p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1">Leads / campañas vinculados ({c.leads.length})</p>
            {c.leads.map((l, i) => (
              <p key={i} className="text-[11px] text-[var(--text)]">• {l.nombre || 'Lead'} — {l.plataforma || 'canal'} {l.campana ? `· Campaña: ${l.campana}` : ''}{l.anuncio ? ` · Anuncio: ${l.anuncio}` : ''} · {String(l.fecha).slice(0, 10)}</p>
            ))}
          </div>
        )}
        <div className="border border-[var(--border)] rounded-lg p-2" data-testid="cli-ficha-presupuestos">
          <p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1">Presupuestos SA ({(data.presupuestos || []).length})</p>
          {(data.presupuestos || []).map((p) => (
            <p key={p.id} className="text-[11px] text-[var(--text)]">• <b>{p.numero}</b> — {p.nombre} · {p.estado} · {p.vendor_name || ''} · {String(p.created_at).slice(0, 10)}</p>
          ))}
          {(data.presupuestos || []).length === 0 && <p className="text-[11px] text-[var(--text-muted)]">Sin presupuestos.</p>}
        </div>
        <TimelineCliente cid={cid} />
      </DialogContent>
    </Dialog>
  );
};
